import type { CostPurchaseRequestType } from '@/modules/main/carrier/edit/components/components/tabs/cost-purchase/costPurchaseSchema';
import type { MoreInfoSchemaOutput } from '@/modules/main/carrier/edit/components/components/tabs/more-info/moreInfoSchema';
import type { AddBookingRequestType } from '@/modules/main/carrier/view/components/components/add-booking/addBookingSchema';
import type { AddQuoteRequestType } from '@/modules/main/carrier/view/components/tabs/components/bookingInfo/components/add-quote/addQuoteSchema';
import { carrierModule } from '@/module/carrierModule';
import ApiService, { type RequestOptions } from '@/services/apiService';
import type {
  PaginatedListCarriers,
  AddCarrierRequestType,
  AddQuickVehicleResponseInterface,
  AddSellerResponseInterface,
  CountryInterface,
  DealerSellerAuctionInterface,
  PatchQuotationRequestInterface,
  PatchVitalsRequestType,
  DataInterface,
  ModelOptionsInterface,
  CustomerInterface,
  AddCarrierResponseInterface,
  CarrierByIdForViewInterface,
  CarrierForEditResponse,
  BookingInfoResponse,
  CategoriesInterface,
  ExportCategoriesInterface,
  LocationInterface,
  ImageByCarrierId,
  ModifyImageRequestInterface,
  ModifyImageSequenceInterface,
  CarrierDocument,
  CarrierHistory,
  ShareByEmailRequestInterface,
  CarrierMaster,
  DeliverableCarrier,
  ConfirmDeliveryOfDocumentsRequestI,
  MakeResponse,
  MakeItem,
  ModelResponse,
  AddModelResponse,
  MasterSellerResponseI,
  NullableAddUpdateSellerRequestT,
  AddUpdateSellerResponseI,
  MasterDealerResponseI,
  NullableAddUpdateDealerRequestT,
  AddUpdateDealerResponseI,
  CarriersFromSavedQueue,
  ShareByEmailMultiRequestInterface,
  UploadDealerStockRequestInterface,
  UploadDealerStockResponseInterface,
} from '@/types/carrier-types';
import type { StoreBaseResponse } from '@/types/types';
import { handleResult, isJson } from '@/helpers/utils/functionality-utils';
import type { AddSellerRequestType } from '@/modules/main/carrier/add/components/components/components/AddSeller/AddSellerSchema';
import type { CarrierFiltersSchemaType } from '@/modules/main/carrier/list/components/filter/filtersSchema';
import type { RecursiveNullable } from '@/types/component';
import type AuctionHouseResponse from '@/types/carrier-types';
import type { AddQuickVehicleRequestType } from '@/modules/export/auction/list/components/AddQuickRiksoPopup/addQuickVehicleSchema';

export class CarrierService {
  private carrierApiService: ApiService;

  constructor() {
    this.carrierApiService = new ApiService(carrierModule);
  }

  //get all carriers with optional filters
  async getCarriers(params: CarrierFiltersSchemaType['queryParameters']) {
    const response = await this.carrierApiService.get<PaginatedListCarriers>(
      '/api/v1/carrier/list',
      params,
    );
    return handleResult(response);
  }

  // add new carrier
  async addCarrier(params: RecursiveNullable<AddCarrierRequestType>) {
    const response =
      await this.carrierApiService.post<AddCarrierResponseInterface>(
        '/api/v1/carrier/add',
        params,
      );
    return handleResult(response);
  }

  // get carrier by stock number
  async getCarrierByIdForView(stockNo: string) {
    const response =
      await this.carrierApiService.get<CarrierByIdForViewInterface>(
        `/api/v1/carrier/${stockNo}/info`,
      );
    return handleResult(response);
  }

  // get csv for carriers
  async getCarriersForExport(
    params: RequestOptions['params'],
  ): Promise<StoreBaseResponse> {
    const response = await this.carrierApiService.getFile<Blob>(
      '/api/v1/carrier/csv/export',
      params,
      { responseType: 'blob' },
    );
    const result: StoreBaseResponse = {
      error: false,
      message: '',
      status_code: 0,
    };

    if (await isJson(response.data)) {
      const errorText = await response.data.text();
      const errorJson = JSON.parse(errorText);
      result.error = true;
      result.message = errorJson.message;
      return result;

      // Handle the error accordingly
    } else if (response?.data instanceof Blob) {
      const filename =
        response.headers['content-disposition'].split('filename=')[1];

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      result.data = 'fetched Carriers';
      result.error = false;
      result.message = 'Carriers fetched successfully';
    }
    return result;
  }

  // get carrier by id For Edit
  async getCarrierByIdForEdit(id: string) {
    const response = await this.carrierApiService.get<CarrierForEditResponse>(
      `/api/v1/carrier/${id}/edit`,
    );
    return handleResult(response);
  }

  async getBookingInfo(carrierId: number | string) {
    const response = await this.carrierApiService.get<BookingInfoResponse>(
      `/api/v1/carrier/${carrierId}/booking`,
    );
    return handleResult(response);
  }

  async getDealers() {
    const response = await this.carrierApiService.get<
      DealerSellerAuctionInterface[]
    >('/api/v1/master/dealers');
    return handleResult(response);
  }

  async patchVitals(data: PatchVitalsRequestType) {
    const response = await this.carrierApiService.patch<null>(
      '/api/v1/carrier/update/vitals',
      data,
    );
    return handleResult(response);
  }

  async patchMoreInfo(data: MoreInfoSchemaOutput) {
    const response = await this.carrierApiService.patch<null>(
      '/api/v1/carrier/update/more-info',
      data,
    );
    return handleResult(response);
  }

  async patchCosts(data: CostPurchaseRequestType) {
    const response = await this.carrierApiService.patch<null>(
      '/api/v1/carrier/update/costs',
      data,
    );
    return handleResult(response);
  }

  async addQuote(data: AddQuoteRequestType) {
    const response = await this.carrierApiService.post<null>(
      '/api/v1/carrier/quote/add',
      data,
    );
    return handleResult(response);
  }

  async patchQuote(data: PatchQuotationRequestInterface) {
    const response = await this.carrierApiService.patch(
      '/api/v1/carrier/quote/update',
      data,
    );
    return handleResult(response);
  }

  async putBooking(data: AddBookingRequestType) {
    const response = await this.carrierApiService.put<null>(
      '/api/v1/carrier/book/customer',
      data,
    );
    return handleResult(response);
  }

  async addQuickVehicle(data: AddQuickVehicleRequestType) {
    const response =
      await this.carrierApiService.post<AddQuickVehicleResponseInterface>(
        '/api/v1/carrier/quick/add',
        data,
      );
    return handleResult(response);
  }

  async addSeller(data: AddSellerRequestType) {
    const response =
      await this.carrierApiService.post<AddSellerResponseInterface>(
        '/api/v1/master/seller/add',
        data,
      );
    return handleResult(response);
  }

  // masters
  // status type
  async getStatusTypes() {
    const response = await this.carrierApiService.get<{
      [key: string]: string;
    }>('/api/v1/master/booking/types');
    return handleResult(response);
  }
  // carrier types
  async getCarrierTypes() {
    const response = await this.carrierApiService.get<DataInterface[]>(
      '/api/v1/master/carrier/types',
    );
    return handleResult(response);
  }

  //fuel types
  async getFuelTypes() {
    const response = await this.carrierApiService.get<DataInterface[]>(
      '/api/v1/master/fuel/types',
    );
    return handleResult(response);
  }

  // makes
  async getCarrierMakes() {
    const response = await this.carrierApiService.get<DataInterface[]>(
      '/api/v1/master/carrier-makes',
    );
    return handleResult(response);
  }

  //models
  async getCarrierModels() {
    const response = await this.carrierApiService.get<ModelOptionsInterface[]>(
      '/api/v1/master/carrier-models',
    );
    return handleResult(response);
  }

  //categories
  async getCarrierCategories() {
    const response = await this.carrierApiService.get<CategoriesInterface[]>(
      '/api/v1/master/carrier/categories',
    );
    return handleResult(response);
  }

  // export categories
  async getCarrierExportCategories() {
    const response = await this.carrierApiService.get<
      ExportCategoriesInterface[]
    >('/api/v1/master/carrier/export/categories');
    return handleResult(response);
  }

  // transmission types
  async getTransmissionTypes() {
    const response = await this.carrierApiService.get<DataInterface[]>(
      '/api/v1/master/transmission/types',
    );
    return handleResult(response);
  }

  // locations
  async getLocations() {
    const response = await this.carrierApiService.get<LocationInterface[]>(
      '/api/v1/master/locations',
    );
    return handleResult(response);
  }

  // auction houses
  async getAuctionHouses() {
    const response = await this.carrierApiService.get<
      DealerSellerAuctionInterface[]
    >('/api/v1/master/auction-houses');
    return handleResult(response);
  }

  // sellers
  async getSellers() {
    const response = await this.carrierApiService.get<
      DealerSellerAuctionInterface[]
    >('/api/v1/master/sellers');
    return handleResult(response);
  }

  // status
  async getCarrierStatus() {
    const response = await this.carrierApiService.get<DataInterface[]>(
      '/api/v1/master/status/types',
    );
    return handleResult(response);
  }

  // customers
  async getCustomers() {
    const response = await this.carrierApiService.get<CustomerInterface[]>(
      '/api/v1/master/customers',
    );
    return handleResult(response);
  }

  // get Countries
  async getCountries() {
    const response = await this.carrierApiService.get<CountryInterface[]>(
      '/api/v1/master/countries',
    );
    return handleResult(response);
  }

  // get Currencies
  async getCurrencies() {
    const response = await this.carrierApiService.get<string[]>(
      '/api/v1/master/currencies',
    );
    return handleResult(response);
  }

  async getImagesByCarrierId(carrierId: string) {
    const response = await this.carrierApiService.get<ImageByCarrierId[]>(
      `/api/v1/carrier/${carrierId}/images`,
    );
    return handleResult(response);
  }

  async uploadImages(
    data: FormData,
    uploadProgress: (percent: number) => void,
  ) {
    const carrierId = data.get('carrier_id');
    data.delete('carrier_id');
    const response = await this.carrierApiService.postUpload<null>(
      `/api/v1/carrier/${carrierId}/images/upload`,
      data,
      (percent) => {
        uploadProgress(percent);
      },
    );
    return handleResult(response);
  }

  async modifyImageSettings(data: ModifyImageRequestInterface) {
    const response = await this.carrierApiService.patch<null>(
      '/api/v1/carrier/image/modify',
      data,
    );
    return handleResult(response);
  }
  async modifyImageSequence(data: ModifyImageSequenceInterface) {
    const response = await this.carrierApiService.patch<null>(
      '/api/v1/carrier/image/re-sequence',
      data,
    );
    return handleResult(response);
  }

  async getCarrierDocuments(carrierId: string) {
    const response = await this.carrierApiService.get<CarrierDocument[]>(
      `/api/v1/document/${carrierId}/list`,
    );
    return handleResult(response);
  }

  async downloadCarrierDocument(docId: number) {
    const response = await this.carrierApiService.getFile<Blob>(
      `/api/v1/document/${docId}/download`,
      undefined,
      { responseType: 'blob' },
    );
    const contentType = response.headers['content-type'];
    if (String(contentType)?.includes('application/json')) {
      const errorText = await response?.data?.text();
      const errorJson = JSON.parse(errorText);
      return handleResult({ ...response, data: errorJson });
    }
    if (response?.data instanceof Blob) {
      const blob = new Blob([response.data], {
        type: String(contentType) || 'application/octet-stream',
      });
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      const filename = filenameMatch ? filenameMatch[1] : 'abc';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return handleResult({
        ...response,
        data: {
          status_code: 200,
          error: '',
          message: 'File Received Successfully',
          data: null,
        },
      });
    }
    //@ts-expect-error this won't run, it's here to satisfy types
    return handleResult(response);
  }

  async downloadCarrierDocument_preview(docId: number) {
    const response = await this.carrierApiService.getFile<Blob>(
      `/api/v1/document/${docId}/download`,
      undefined,
      { responseType: 'blob' },
    );
    const contentType = response.headers['content-type'];
    if (String(contentType)?.includes('application/json')) {
      const errorText = await response?.data?.text();
      const errorJson = JSON.parse(errorText);
      return handleResult({ ...response, data: errorJson });
    }
    if (response?.data instanceof Blob) {
      const blob = new Blob([response.data], {
        type: String(contentType) || 'application/octet-stream',
      });
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      const filename = filenameMatch ? filenameMatch[1] : 'abc';

      const url = window.URL.createObjectURL(blob);

      return handleResult({
        ...response,
        data: {
          status_code: 200,
          error: '',
          message: 'File Received Successfully',
          data: {
            url,
            filename,
          },
        },
      });
    }
    //@ts-expect-error this won't run, it's here to satisfy types
    return handleResult(response);
  }

  async addCarrierDocument(formData: FormData) {
    const carrierId = formData.get('carrierId');
    const docType = formData.get('docType');
    formData.delete('carrierId');
    formData.delete('docType');
    const response = await this.carrierApiService.post<string>(
      `/api/v1/document/carrier/${carrierId}/${docType}/doc/upload`,
      formData,
    );
    return handleResult(response);
  }

  async deleteCarrierDocument(carrierId: number, docId: number) {
    const response = await this.carrierApiService.delete<null>(
      `/api/v1/document/${docId}/carrier/${carrierId}/doc/remove`,
    );
    return handleResult(response);
  }

  async getCarrierHistory(carrierId: string) {
    const response = await this.carrierApiService.get<CarrierHistory[]>(
      `/api/v1/carrier/${carrierId}/history`,
    );
    return handleResult(response);
  }

  async shareByEmail(data: ShareByEmailRequestInterface) {
    const response = await this.carrierApiService.put<null>(
      `/api/v1/carrier/email/share`,
      data,
    );
    return handleResult(response);
  }

  async shareByEmailMultiCarriers(data: ShareByEmailMultiRequestInterface) {
    const response = await this.carrierApiService.put<null>(
      `/api/v1/carrier/email/list/share`,
      data,
    );
    return handleResult(response);
  }

  async getAllCarriers() {
    const response = await this.carrierApiService.get<CarrierMaster[]>(
      `/api/v1/master/carrier/stocks`,
    );
    return handleResult(response);
  }

  async getDeliverableCarriers(params: RequestOptions['params']) {
    const response = await this.carrierApiService.get<DeliverableCarrier[]>(
      `/api/v1/carrier/deliverable/carriers`,
      params,
    );
    return handleResult(response);
  }

  async markStatusAsDelivered(carrierId: number) {
    const response = await this.carrierApiService.get<null>(
      `/api/v1/carrier/${carrierId}/ready-to-deliver`,
    );
    return handleResult(response);
  }

  async confirmDeliveryOfDocuments(body: ConfirmDeliveryOfDocumentsRequestI) {
    const response = await this.carrierApiService.post<null>(
      `/api/v1/carrier/confirm/delivery`,
      body,
    );
    return handleResult(response);
  }

  //make
  async getMasterCarrierMake() {
    const response = await this.carrierApiService.get<MakeResponse>(
      '/api/v1/master/carrier/make/list',
    );
    return handleResult(response);
  }
  async addMasterCarrierMake(data: MakeItem) {
    const response = await this.carrierApiService.post<MakeResponse>(
      '/api/v1/master/carrier/make/upsert',
      data,
    );
    return handleResult(response);
  }
  async getMasterCarrierModel(data: ModelResponse) {
    const response = await this.carrierApiService.put<ModelResponse>(
      '/api/v1/master/carrier/model/list',
      data,
    );
    return handleResult(response);
  }
  async addMasterCarrierModel(data: AddModelResponse) {
    const response = await this.carrierApiService.post<AddModelResponse>(
      '/api/v1/master/carrier/model/upsert',
      data,
    );
    return handleResult(response);
  }
  // Auction House
  async getMasterAuctinHouse() {
    const response = await this.carrierApiService.get<AuctionHouseResponse>(
      '/api/v1/master/auction/house/list',
    );
    return handleResult(response);
  }

  async getMasterSellers() {
    const response = await this.carrierApiService.get<MasterSellerResponseI>(
      '/api/v1/master/seller/list',
    );
    return handleResult(response);
  }

  async addUpdateSeller(data: NullableAddUpdateSellerRequestT) {
    const response =
      await this.carrierApiService.post<AddUpdateSellerResponseI>(
        '/api/v1/master/seller/upsert',
        data,
      );
    return handleResult(response);
  }

  async getMasterDealers() {
    const response = await this.carrierApiService.get<MasterDealerResponseI>(
      '/api/v1/master/dealer/list',
    );
    return handleResult(response);
  }

  async addUpdateDealer(data: NullableAddUpdateDealerRequestT) {
    const response =
      await this.carrierApiService.post<AddUpdateDealerResponseI>(
        '/api/v1/master/dealer/upsert',
        data,
      );
    return handleResult(response);
  }

  async downloadCarrierImagesAsZip(carrierId: number) {
    const response = await this.carrierApiService.getFile<Blob>(
      `/api/v1/carrier/${carrierId}/image/download`,
      undefined,
      { responseType: 'blob' },
    );
    const contentType = response.headers['content-type'];
    if (String(contentType)?.includes('application/json')) {
      const errorText = await response?.data?.text();
      const errorJson = JSON.parse(errorText);
      return handleResult({ ...response, data: errorJson });
    }
    if (response?.data instanceof Blob) {
      const blob = new Blob([response.data], {
        type: String(contentType) || 'application/octet-stream',
      });
      const contentDisposition = response.headers['content-disposition'] || '';
      const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
      const filename = filenameMatch ? filenameMatch[1] : 'abc';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      return handleResult({
        ...response,
        data: {
          status_code: 200,
          error: '',
          message: 'File Received Successfully',
          data: null,
        },
      });
    }
    //@ts-expect-error this won't run, it's here to satisfy types
    return handleResult(response);
  }

  async getCarriersFromQueueId(queueId: string) {
    const response = await this.carrierApiService.get<CarriersFromSavedQueue[]>(
      `/api/v1/carrier/queue/${queueId}/info`,
    );
    return handleResult(response);
  }

  async uploadExcelDealerStock(formData: FormData) {
    const dealerId = formData.get('dealerId');
    formData.delete('dealerId');
    const response = await this.carrierApiService.post<
      UploadDealerStockResponseInterface[]
    >(`/api/v1/dealer/${dealerId}/upload/stock`, formData);
    return handleResult(response);
  }

  async updateDealerStock(payload: UploadDealerStockRequestInterface) {
    const response = await this.carrierApiService.post<null>(
      '/api/v1/dealer/inventory/upsert',
      payload,
    );
    return handleResult(response);
  }
}
